import { useEffect, useRef, useState, type FormEvent } from "react";
import { CoreApiError } from "../../core-api/api-client";
import {
  GMAIL_READONLY_SCOPES,
  revokeGmailAccess,
  startGmailOAuth,
} from "../../core-api/gmail";
import {
  createMailbox,
  deleteMailbox,
  editMailbox,
  loadMailboxes,
  mailboxRoles,
  setMailboxEnabled,
  syncMailbox,
  type MailboxDirectory,
  type MailboxRole,
  type ManagedMailbox,
} from "./api";
import "./mailboxes.css";

const labels: Record<string, string> = {
  ACTIVE: "已启用",
  INACTIVE: "等待授权",
  SUSPENDED: "已暂停",
  AUTHORIZED: "已授权",
  NOT_CONNECTED: "未授权",
  REVOKED: "已撤销",
  EXPIRED: "授权已过期",
  IDLE: "空闲",
  SYNCING: "同步中",
  RECOVERY_REQUIRED: "需要恢复同步",
  FAILED: "同步失败",
};
const date = (value: string | null | undefined) =>
  value
    ? new Date(value).toLocaleString("zh-CN", { hour12: false })
    : "暂无记录";
function describe(error: unknown) {
  if (error instanceof CoreApiError) {
    if (error.code?.startsWith("PASSWORD_CONFIRMATION_"))
      return error.reason ?? "请输入当前 CRM 登录密码确认本次操作。";
    if (error.code === "STEP_UP_REQUIRED")
      return "身份验证已过期，请重新登录后再授权邮箱。";
    if (error.status === 403)
      return "当前账号没有此操作权限，或需要重新登录完成身份验证。";
    if (error.status === 401) return "登录已过期，请重新登录。";
  }
  return error instanceof Error ? error.message : "操作失败，请重试。";
}

export function MailboxConfiguration() {
  const [directory, setDirectory] = useState<MailboxDirectory>();
  const [selectedId, setSelectedId] = useState("");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<MailboxRole | "">("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [authorization, setAuthorization] = useState<{
    mailboxId: string;
    url: string;
  }>();
  const [confirm, setConfirm] = useState<"disable" | "delete" | "revoke">();
  const [passwordRequest, setPasswordRequest] = useState<{
    mailbox: ManagedMailbox;
    action: "authorize" | "revoke";
  }>();
  const [password, setPassword] = useState("");
  const generation = useRef(0);
  const selected = directory?.items.find((item) => item.id === selectedId);
  const capabilities = directory?.capabilities;
  const reload = async () => {
    const version = generation.current;
    const next = await loadMailboxes();
    if (version !== generation.current) return;
    setDirectory(next);
    setSelectedId((id) =>
      next.items.some((item) => item.id === id)
        ? id
        : (next.items[0]?.id ?? ""),
    );
  };
  useEffect(() => {
    const controller = new AbortController();
    void loadMailboxes(controller.signal)
      .then((next) => {
        if (controller.signal.aborted) return;
        setDirectory(next);
        setSelectedId(next.items[0]?.id ?? "");
      })
      .catch((cause) => {
        if (!controller.signal.aborted) setError(describe(cause));
      });
    return () => {
      controller.abort();
      generation.current++;
    };
  }, []);
  const run = async (action: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError("");
    setNotice("");
    const version = generation.current;
    try {
      await action();
    } catch (cause) {
      if (version === generation.current) setError(describe(cause));
    } finally {
      if (version === generation.current) setBusy(false);
    }
  };
  const openEditor = (item?: ManagedMailbox) => {
    setPasswordRequest(undefined);
    setPassword("");
    setCreating(!item);
    setEditing(true);
    setConfirm(undefined);
    setError("");
    setEmail(item?.emailAddress ?? "");
    setRole(item?.roleType ?? "");
    setNote(item?.note ?? "");
  };
  const save = (event: FormEvent) => {
    event.preventDefault();
    if (!role || (!creating && !selected)) return;
    void run(async () => {
      const saved = creating
        ? await createMailbox({
            emailAddress: email.trim(),
            roleType: role,
            note,
          })
        : await editMailbox(selected!, role, note);
      await reload();
      setSelectedId(saved.id);
      setEditing(false);
      setNotice(
        creating ? "邮箱已创建，请完成 Gmail 授权。" : "邮箱配置已保存。",
      );
      setCreating(false);
    });
  };
  const performOAuth = async (
    item: ManagedMailbox,
    action: "authorize" | "revoke",
    confirmationPassword?: string,
  ) => {
    try {
      if (action === "authorize") {
        const result = await startGmailOAuth(
          item.id,
          item.gmailAuthorization?.scopes ?? GMAIL_READONLY_SCOPES,
          item.gmailAuthorization?.redirectUri ?? undefined,
          confirmationPassword,
        );
        const url = new URL(result.authorizationUrl);
        if (url.protocol !== "https:" || url.hostname !== "accounts.google.com")
          throw new Error("授权地址无效，请联系管理员。");
        setAuthorization({ mailboxId: item.id, url: url.href });
      } else {
        const result = await revokeGmailAccess(item.id, confirmationPassword);
        setAuthorization(undefined);
        setNotice(
          result.googleRevoked
            ? "Gmail 授权已撤销，历史邮件保留。"
            : "CRM 已停止访问邮箱。Google 端撤销未确认，请到 Google 账号的第三方连接中检查。",
        );
        await reload();
      }
      setPasswordRequest(undefined);
    } catch (cause) {
      if (
        cause instanceof CoreApiError &&
        cause.code?.startsWith("PASSWORD_CONFIRMATION_")
      ) {
        setPasswordRequest({ mailbox: item, action });
        setConfirm(undefined);
      }
      throw cause;
    } finally {
      setPassword("");
    }
  };
  const startAuthorization = (item: ManagedMailbox) =>
    void run(() => performOAuth(item, "authorize"));
  const confirmAction = () => {
    if (!selected) return;
    void run(async () => {
      if (confirm === "disable") {
        await setMailboxEnabled(selected, false);
        setNotice("邮箱已禁用，后续同步和发送会被阻止。");
      } else if (confirm === "delete") {
        await deleteMailbox(selected);
        setNotice("邮箱已删除，历史邮件仍保留。");
      } else if (confirm === "revoke") {
        await performOAuth(selected, "revoke");
      }
      setConfirm(undefined);
      await reload();
    });
  };
  const filtered =
    directory?.items.filter((item) =>
      `${item.emailAddress} ${item.note ?? ""} ${item.roleType ? mailboxRoles[item.roleType] : "待分类"}`
        .toLowerCase()
        .includes(search.toLowerCase()),
    ) ?? [];
  const scopes = selected?.gmailAuthorization?.scopes ?? GMAIL_READONLY_SCOPES;
  const canSync =
    selected?.status === "ACTIVE" &&
    selected.authorizationStatus === "AUTHORIZED" &&
    !selected.disabledAt &&
    selected.syncStatus !== "SYNCING";
  const canAuthorize =
    selected?.provider === "GMAIL" &&
    selected.gmailAuthorization?.providerEnabled !== false &&
    (selected.authorizationStatus !== "AUTHORIZED" ||
      selected.gmailAuthorization?.reauthorizationEnabled);
  return (
    <section className="mailbox-workspace" aria-label="邮箱配置和授权">
      <header className="mailbox-header">
        <div>
          <h1>邮箱配置和授权</h1>
          <p>管理当前工作空间的邮箱、授权与同步</p>
        </div>
        <div className="mailbox-actions">
          <button disabled={busy} onClick={() => void run(reload)}>
            刷新状态
          </button>
          {capabilities?.manage && (
            <button
              className="primary"
              disabled={busy}
              onClick={() => openEditor()}
            >
              新增邮箱
            </button>
          )}
        </div>
      </header>
      {error && (
        <div role="alert" className="mailbox-error">
          {error}
        </div>
      )}
      {notice && (
        <div role="status" className="mailbox-notice">
          {notice}
        </div>
      )}
      {passwordRequest && (
        <form
          className="mailbox-password"
          aria-label="确认邮箱操作身份"
          onSubmit={(event) => {
            event.preventDefault();
            const value = password;
            setPassword("");
            void run(() =>
              performOAuth(
                passwordRequest.mailbox,
                passwordRequest.action,
                value,
              ),
            );
          }}
        >
          <h3>确认本次邮箱操作</h3>
          <p>
            {passwordRequest.mailbox.emailAddress} ·{" "}
            {passwordRequest.action === "authorize" ? "连接 Gmail" : "撤销授权"}
          </p>
          <label>
            当前 CRM 登录密码
            <input
              type="password"
              autoComplete="current-password"
              required
              minLength={6}
              maxLength={512}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={busy}
            />
          </label>
          <p className="mailbox-muted">
            输入你登录 CRM 的密码。本次确认只用于上述邮箱操作。
          </p>
          <div className="mailbox-actions">
            <button
              className="primary"
              disabled={busy || !password}
              type="submit"
            >
              验证并继续
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setPasswordRequest(undefined);
                setPassword("");
                setError("");
              }}
            >
              取消验证
            </button>
          </div>
        </form>
      )}
      {!directory ? (
        <p>{error ? "邮箱暂时无法加载，请刷新重试。" : "正在加载邮箱…"}</p>
      ) : (
        <div className="mailbox-columns">
          <aside className="mailbox-list">
            <label className="mailbox-search">
              搜索邮箱
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="邮箱地址、类型或备注"
              />
            </label>
            <p className="mailbox-muted">{filtered.length} 个邮箱</p>
            {!filtered.length && (
              <p className="mailbox-empty">
                {search ? "没有匹配的邮箱" : "当前工作空间尚未配置邮箱"}
              </p>
            )}
            {filtered.map((item) => (
              <button
                key={item.id}
                className={`mailbox-item ${selectedId === item.id && !creating ? "selected" : ""}`}
                disabled={busy}
                onClick={() => {
                  setSelectedId(item.id);
                  setEditing(false);
                  setCreating(false);
                  setConfirm(undefined);
                  setError("");
                }}
              >
                <strong>{item.emailAddress}</strong>
                <span>
                  {item.roleType ? mailboxRoles[item.roleType] : "待分类"} ·{" "}
                  {item.provider}
                </span>
                <span className="mailbox-item-status">
                  <i
                    className={
                      item.authorizationStatus === "AUTHORIZED" &&
                      !item.disabledAt
                        ? "connected"
                        : ""
                    }
                  />
                  {item.disabledAt
                    ? "已禁用"
                    : (labels[item.authorizationStatus] ??
                      item.authorizationStatus)}
                </span>
              </button>
            ))}
          </aside>
          <main className="mailbox-detail">
            {editing ? (
              <form onSubmit={save} className="mailbox-form">
                <h2>{creating ? "新增邮箱" : "修改邮箱配置"}</h2>
                <label>
                  邮箱地址
                  <input
                    type="email"
                    required
                    maxLength={320}
                    value={email}
                    readOnly={!creating}
                    disabled={busy}
                    onChange={(event) => setEmail(event.target.value)}
                    autoComplete="off"
                  />
                </label>
                {!creating && (
                  <p className="mailbox-muted">
                    邮箱地址和工作空间创建后不可修改。
                  </p>
                )}
                <label>
                  邮箱类型
                  <select
                    required
                    value={role}
                    disabled={busy}
                    onChange={(event) =>
                      setRole(event.target.value as MailboxRole)
                    }
                  >
                    <option value="">请选择类型</option>
                    {Object.entries(mailboxRoles).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  备注（可选）
                  <textarea
                    maxLength={2000}
                    rows={4}
                    value={note}
                    disabled={busy}
                    onChange={(event) => setNote(event.target.value)}
                  />
                </label>
                <div className="mailbox-actions">
                  <button
                    type="submit"
                    className="primary"
                    disabled={busy || !role}
                  >
                    {busy ? "保存中…" : "保存邮箱"}
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setEditing(false);
                      setCreating(false);
                    }}
                  >
                    取消
                  </button>
                </div>
              </form>
            ) : selected ? (
              <>
                <div className="mailbox-detail-title">
                  <div>
                    <h2>{selected.emailAddress}</h2>
                    <p>
                      {selected.roleType
                        ? mailboxRoles[selected.roleType]
                        : "待分类"}{" "}
                      · {selected.provider}
                    </p>
                  </div>
                  {capabilities?.manage && (
                    <button
                      disabled={busy}
                      onClick={() => openEditor(selected)}
                    >
                      修改配置
                    </button>
                  )}
                </div>
                <div className="mailbox-status-grid">
                  {[
                    [
                      "业务状态",
                      selected.disabledAt ? "已禁用" : labels[selected.status],
                    ],
                    ["授权状态", labels[selected.authorizationStatus]],
                    ["同步状态", labels[selected.syncStatus]],
                  ].map(([key, value]) => (
                    <div key={key}>
                      <span>{key}</span>
                      <strong>{value}</strong>
                    </div>
                  ))}
                </div>
                <section className="mailbox-section">
                  <h3>Gmail 授权</h3>
                  <p>
                    {selected.authorizationStatus === "AUTHORIZED"
                      ? "邮箱已连接。权限变更后请刷新状态。"
                      : "通过 Google 官方授权连接此邮箱，请选择与上方地址一致的 Google 账号。"}
                  </p>
                  <p className="mailbox-muted">
                    申请权限：
                    {scopes.includes(
                      "https://www.googleapis.com/auth/gmail.modify",
                    )
                      ? "读取邮件和修改邮件状态"
                      : "读取邮件"}
                    {scopes.includes(
                      "https://www.googleapis.com/auth/gmail.send",
                    )
                      ? "、发送邮件"
                      : ""}
                    。
                  </p>
                  {selected.gmailAuthorization?.providerEnabled === false && (
                    <p>服务端尚未启用 Gmail 接入。</p>
                  )}
                  <div className="mailbox-actions">
                    {capabilities?.authorize && (
                      <button
                        className="primary"
                        disabled={busy || !canAuthorize}
                        onClick={() => startAuthorization(selected)}
                      >
                        {["AUTHORIZED", "EXPIRED"].includes(
                          selected.authorizationStatus,
                        )
                          ? "重新授权"
                          : "连接 Gmail"}
                      </button>
                    )}
                    {capabilities?.revoke &&
                      selected.authorizationStatus !== "NOT_CONNECTED" && (
                        <button
                          disabled={busy}
                          onClick={() => setConfirm("revoke")}
                        >
                          撤销授权
                        </button>
                      )}
                  </div>
                  {authorization?.mailboxId === selected.id && (
                    <div className="mailbox-oauth">
                      <a
                        href={authorization.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        前往 Google 完成授权 ↗
                      </a>
                      <p>
                        授权完成后返回此页，点击“刷新状态”。链接过期时请重新点击连接。
                      </p>
                    </div>
                  )}
                </section>
                <section className="mailbox-section">
                  <h3>邮件同步</h3>
                  <p>
                    {selected.syncStatus === "RECOVERY_REQUIRED"
                      ? "历史游标需要恢复，将重新核对最近 30 天邮件。"
                      : !selected.historyCursor
                        ? "首次同步获取最近 24 小时的邮件，后续使用增量同步。"
                        : "增量同步获取上次成功同步后的邮件变化。"}
                  </p>
                  {capabilities?.sync && (
                    <button
                      disabled={busy || !canSync}
                      onClick={() =>
                        void run(async () => {
                          await syncMailbox(selected);
                          await reload();
                          setNotice("同步任务已提交，请稍后刷新查看结果。");
                        })
                      }
                    >
                      {selected.syncStatus === "RECOVERY_REQUIRED"
                        ? "恢复同步"
                        : !selected.historyCursor
                          ? "首次同步"
                          : "立即同步"}
                    </button>
                  )}
                  {!canSync && (
                    <p className="mailbox-muted">
                      {selected.syncStatus === "SYNCING"
                        ? "同步正在进行中。"
                        : "请先启用邮箱并完成授权。"}
                    </p>
                  )}
                </section>
                <dl className="mailbox-facts">
                  <div>
                    <dt>创建时间</dt>
                    <dd>{date(selected.createdAt)}</dd>
                  </div>
                  <div>
                    <dt>最近 CRM 发信</dt>
                    <dd>{date(selected.lastSentAt)}</dd>
                  </div>
                  <div>
                    <dt>最近授权</dt>
                    <dd>{date(selected.authorizedAt)}</dd>
                  </div>
                  <div>
                    <dt>成功同步</dt>
                    <dd>{date(selected.lastSuccessfulSyncAt)}</dd>
                  </div>
                  <div>
                    <dt>恢复同步</dt>
                    <dd>{date(selected.lastRecoverySyncAt)}</dd>
                  </div>
                  <div>
                    <dt>备注</dt>
                    <dd>{selected.note || "无"}</dd>
                  </div>
                </dl>
                {capabilities?.manage && (
                  <section className="mailbox-section">
                    <h3>邮箱使用状态</h3>
                    <p>禁用后停止后续收发任务，历史邮件继续保留。</p>
                    <div className="mailbox-actions">
                      <button
                        disabled={busy}
                        onClick={() =>
                          selected.disabledAt
                            ? void run(async () => {
                                await setMailboxEnabled(selected, true);
                                await reload();
                                setNotice(
                                  "邮箱已启用；如尚未授权，请连接 Gmail。",
                                );
                              })
                            : setConfirm("disable")
                        }
                      >
                        {selected.disabledAt ? "启用邮箱" : "禁用邮箱"}
                      </button>
                      <button
                        className="danger"
                        disabled={
                          busy ||
                          !selected.disabledAt ||
                          ["AUTHORIZED", "EXPIRED"].includes(
                            selected.authorizationStatus,
                          )
                        }
                        title="先禁用并撤销授权后可删除"
                        onClick={() => setConfirm("delete")}
                      >
                        删除邮箱
                      </button>
                    </div>
                    <p className="mailbox-muted">
                      删除前需先禁用并撤销授权。删除后保留邮件历史，不可用同地址重复新建。
                    </p>
                  </section>
                )}
                {confirm && (
                  <div
                    className="mailbox-confirm"
                    role="alertdialog"
                    aria-label="确认邮箱操作"
                  >
                    <h3>
                      {confirm === "disable"
                        ? "禁用邮箱"
                        : confirm === "delete"
                          ? "删除邮箱"
                          : "撤销 Gmail 授权"}
                    </h3>
                    <p>
                      确认对 {selected.emailAddress} 执行此操作？
                      {confirm === "delete"
                        ? "邮箱将从配置列表移除，历史邮件保留。"
                        : "后续收发任务将停止，历史邮件保留。"}
                    </p>
                    <div className="mailbox-actions">
                      <button
                        className="danger"
                        disabled={busy}
                        onClick={confirmAction}
                      >
                        确认操作
                      </button>
                      <button
                        disabled={busy}
                        onClick={() => setConfirm(undefined)}
                      >
                        取消操作
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="mailbox-empty">
                <h2>配置工作空间邮箱</h2>
                <p>
                  {capabilities?.manage
                    ? "点击“新增邮箱”，填写地址和类型后完成 Google 授权。"
                    : "当前没有可查看的邮箱，请联系管理员。"}
                </p>
              </div>
            )}
          </main>
        </div>
      )}
    </section>
  );
}
